import pluralize from 'pluralize';

import {
  AbilityToPayEnum,
  CustomerCostingMethodTypeEnum,
  CustomerSizeEnum,
  CustomerStatusEnum,
  CustomerStatusFilterEnum,
  FundingTypeEnum,
  InvoicingCycleEnum,
  PayPeriodEnum,
  ServiceAgreementTypeEnum,
  YearsInBusinessEnum,
} from 'api/backofficeApiClient';
import { DurationTypeEnum } from 'api/marketplaceApiClient';
import {
  BreakInServiceEnum,
  CommissionTypeEnum,
  ContractedMBPServiceLinesEnum,
  ContractRenewalTermEnum,
  CustomerAgreementTypeEnum,
  CustomerPreScreeningProviderEnum,
  IndemnificationTypeEnum,
  PaymentMethodTypeEnum,
  PaymentTermEnum,
} from 'api/adminApiClient';
import { ClientCreationEnum } from 'components/modals/ClientModal/enum';
import { CustomerBulkImportDetailsModel } from 'features/client/ClientBulkImportPage/ClientBulkImportTableSection/interface';

export const getClientStatusName = (
  status?: CustomerStatusFilterEnum | CustomerStatusEnum,
) => {
  switch (status) {
    case CustomerStatusFilterEnum.All:
      return 'All';
    case CustomerStatusFilterEnum.Requested:
    case CustomerStatusFilterEnum.NewCustomerSetupStarted:
    case CustomerStatusFilterEnum.CreditCheckVerified:
    case CustomerStatusFilterEnum.UnderLegalReview:
    case CustomerStatusFilterEnum.AcquiringCertificateOfInsurance:
    case CustomerStatusFilterEnum.ApprovedByFinance:
    case CustomerStatusFilterEnum.ApprovedByLegal:
    case CustomerStatusFilterEnum.CounterSignaturesCompleted:
    case CustomerStatusFilterEnum.StaffOnboardingPackagesCreated:
    case CustomerStatusFilterEnum.FinalAgreementUploaded:
      return 'Pending';
    case CustomerStatusFilterEnum.InProgress:
      return 'In Progress';
    case CustomerStatusFilterEnum.Draft:
      return 'Draft';
    default:
      return '';
  }
};

export const getClientSizeLabel = (clientSize?: CustomerSizeEnum) => {
  switch (clientSize) {
    case CustomerSizeEnum.From0To20:
      return '0-20';
    case CustomerSizeEnum.From21To80:
      return '20-80';
    case CustomerSizeEnum.From81To200:
      return '80-200';
    case CustomerSizeEnum.From201To800:
      return '200-800';
    case CustomerSizeEnum.From801To1500:
      return '800-1500';
    case CustomerSizeEnum.From1501Plus:
      return '1500+';
    default:
      return '';
  }
};

export const getYearsInBusinessLabel = (
  yearsInBusiness?: YearsInBusinessEnum,
) => {
  switch (yearsInBusiness) {
    case YearsInBusinessEnum.DoNotKnow:
      return 'Do not know';
    case YearsInBusinessEnum.From0To1:
      return '0-1';
    case YearsInBusinessEnum.From1To3:
      return '1-3';
    case YearsInBusinessEnum.From3To5:
      return '3-5';
    case YearsInBusinessEnum.From5Plus:
      return '5+';
    default:
      return '';
  }
};

export const getAbilityToPayLabel = (abilityToPay?: AbilityToPayEnum) => {
  switch (abilityToPay) {
    case AbilityToPayEnum.DoNotKnow:
      return 'Do not know';
    case AbilityToPayEnum.Low:
      return 'Low';
    case AbilityToPayEnum.Fair:
      return 'Fair';
    case AbilityToPayEnum.High:
      return 'High';
    default:
      return '';
  }
};

export const getClientSizes = () => [
  {
    value: CustomerSizeEnum.From0To20,
    label: getClientSizeLabel(CustomerSizeEnum.From0To20),
  },
  {
    value: CustomerSizeEnum.From21To80,
    label: getClientSizeLabel(CustomerSizeEnum.From21To80),
  },
  {
    value: CustomerSizeEnum.From81To200,
    label: getClientSizeLabel(CustomerSizeEnum.From81To200),
  },
  {
    value: CustomerSizeEnum.From201To800,
    label: getClientSizeLabel(CustomerSizeEnum.From201To800),
  },
  {
    value: CustomerSizeEnum.From801To1500,
    label: getClientSizeLabel(CustomerSizeEnum.From801To1500),
  },
  {
    value: CustomerSizeEnum.From1501Plus,
    label: getClientSizeLabel(CustomerSizeEnum.From1501Plus),
  },
];

export const getYearsInBusiness = () => [
  {
    value: YearsInBusinessEnum.DoNotKnow,
    label: getYearsInBusinessLabel(YearsInBusinessEnum.DoNotKnow),
  },
  {
    value: YearsInBusinessEnum.From0To1,
    label: getYearsInBusinessLabel(YearsInBusinessEnum.From0To1),
  },
  {
    value: YearsInBusinessEnum.From1To3,
    label: getYearsInBusinessLabel(YearsInBusinessEnum.From1To3),
  },
  {
    value: YearsInBusinessEnum.From3To5,
    label: getYearsInBusinessLabel(YearsInBusinessEnum.From3To5),
  },
  {
    value: YearsInBusinessEnum.From5Plus,
    label: getYearsInBusinessLabel(YearsInBusinessEnum.From5Plus),
  },
];

export const getAbilityToPay = () => [
  {
    value: AbilityToPayEnum.DoNotKnow,
    label: getAbilityToPayLabel(AbilityToPayEnum.DoNotKnow),
  },
  {
    value: AbilityToPayEnum.Low,
    label: getAbilityToPayLabel(AbilityToPayEnum.Low),
  },
  {
    value: AbilityToPayEnum.Fair,
    label: getAbilityToPayLabel(AbilityToPayEnum.Fair),
  },
  {
    value: AbilityToPayEnum.High,
    label: getAbilityToPayLabel(AbilityToPayEnum.High),
  },
];

export const getProjectEstimation = (
  estimatedValue?: number,
  estimatedType?: DurationTypeEnum,
) => {
  if (!estimatedValue && !estimatedType) return;

  switch (estimatedType) {
    case DurationTypeEnum.Week:
      return pluralize('week', estimatedValue, true);

    case DurationTypeEnum.Month:
      return pluralize('month', estimatedValue, true);

    case DurationTypeEnum.Year:
      return pluralize('year', estimatedValue, true);

    default:
      return '';
  }
};

export const checkCustomerStatus = (status: CustomerStatusEnum) => {
  const isDraft = status === CustomerStatusEnum.Draft;
  const isDetails = status === CustomerStatusEnum.NewCustomerSetupComplete;
  const isRequest =
    status >= CustomerStatusEnum.Requested &&
    status < CustomerStatusEnum.NewCustomerSetupComplete;

  return { isDraft, isRequest, isDetails };
};

export const getModalTitle = (type: ClientCreationEnum) => {
  switch (type) {
    case ClientCreationEnum.Create:
      return 'Create new customer';
    case ClientCreationEnum.EditDraft:
      return 'Edit customer draft';
    case ClientCreationEnum.Edit:
      return 'Edit customer';

    default:
      return '';
  }
};

export const getModalSubmitName = (type: ClientCreationEnum) => {
  switch (type) {
    case ClientCreationEnum.EditImport:
      return 'Save';
    case ClientCreationEnum.Create:
    case ClientCreationEnum.EditDraft:
      return 'Create';

    case ClientCreationEnum.Edit:
      return 'Update';

    default:
      return '';
  }
};

export const getCustomerPreScreeningBilledLabel = (
  billedTo?: CustomerPreScreeningProviderEnum,
) => {
  switch (billedTo) {
    case CustomerPreScreeningProviderEnum.Member:
      return 'Member';

    case CustomerPreScreeningProviderEnum.MBP:
      return 'MBP';

    case CustomerPreScreeningProviderEnum.Customer:
      return 'Customer';

    default:
      return '';
  }
};

export const getCustomerCostingMethodTypeLabel = (
  type?: CustomerCostingMethodTypeEnum | null,
) => {
  switch (type) {
    case CustomerCostingMethodTypeEnum.Blended:
      return 'Blended';

    case CustomerCostingMethodTypeEnum.CostPlus:
      return 'Cost +';

    default:
      return '';
  }
};

export const getCustomerPaymentTermsLabel = (term?: PaymentTermEnum) => {
  switch (term) {
    case PaymentTermEnum.DueOnReceipt:
      return 'Due on receipt';
    case PaymentTermEnum.Net5:
      return 'Net 5';
    case PaymentTermEnum.Net7:
      return 'Net 7';
    case PaymentTermEnum.Net10:
      return 'Net 10';
    case PaymentTermEnum.Net15:
      return 'Net 15';
    case PaymentTermEnum.Net21:
      return 'Net 21';
    case PaymentTermEnum.Net22:
      return 'Net 22';
    case PaymentTermEnum.Net30:
      return 'Net 30';
    case PaymentTermEnum.Net45:
      return 'Net 45';
    case PaymentTermEnum.Net60:
      return 'Net 60';
    case PaymentTermEnum.Net75:
      return 'Net 75';
    case PaymentTermEnum.Net90:
      return 'Net 90';
    default:
      return '';
  }
};

export const getCustomerPaymentTermsOptions = () => [
  {
    value: PaymentTermEnum.DueOnReceipt,
    label: getCustomerPaymentTermsLabel(PaymentTermEnum.DueOnReceipt),
  },
  {
    value: PaymentTermEnum.Net5,
    label: getCustomerPaymentTermsLabel(PaymentTermEnum.Net5),
  },
  {
    value: PaymentTermEnum.Net7,
    label: getCustomerPaymentTermsLabel(PaymentTermEnum.Net7),
  },
  {
    value: PaymentTermEnum.Net10,
    label: getCustomerPaymentTermsLabel(PaymentTermEnum.Net10),
  },
  {
    value: PaymentTermEnum.Net15,
    label: getCustomerPaymentTermsLabel(PaymentTermEnum.Net15),
  },
  {
    value: PaymentTermEnum.Net21,
    label: getCustomerPaymentTermsLabel(PaymentTermEnum.Net21),
  },
  {
    value: PaymentTermEnum.Net22,
    label: getCustomerPaymentTermsLabel(PaymentTermEnum.Net22),
  },
  {
    value: PaymentTermEnum.Net30,
    label: getCustomerPaymentTermsLabel(PaymentTermEnum.Net30),
  },
  {
    value: PaymentTermEnum.Net45,
    label: getCustomerPaymentTermsLabel(PaymentTermEnum.Net45),
  },
  {
    value: PaymentTermEnum.Net60,
    label: getCustomerPaymentTermsLabel(PaymentTermEnum.Net60),
  },
  {
    value: PaymentTermEnum.Net75,
    label: getCustomerPaymentTermsLabel(PaymentTermEnum.Net75),
  },
  {
    value: PaymentTermEnum.Net90,
    label: getCustomerPaymentTermsLabel(PaymentTermEnum.Net90),
  },
];

export const getCustomerInvoicingCycleLabel = (
  invoicingCycle?: InvoicingCycleEnum,
) => {
  switch (invoicingCycle) {
    case InvoicingCycleEnum.Weekly:
      return 'Weekly';
    case InvoicingCycleEnum.BiWeekly:
      return 'Bi-Weekly';
    case InvoicingCycleEnum.Monthly:
      return 'Monthly';
    case InvoicingCycleEnum.SemiMonthly:
      return 'Semi-Monthly';
    case InvoicingCycleEnum.SowWeekly:
      return 'SOW Weekly';
    case InvoicingCycleEnum.SowMonthly:
      return 'SOW Monthly';

    default:
      return '';
  }
};

export const getCustomerPayrollCycleLabel = (payrollCycle?: PayPeriodEnum) => {
  switch (payrollCycle) {
    case PayPeriodEnum.BiWeeklyMondaySunday1:
      return 'Bi-Weekly Monday-Sunday 1';
    case PayPeriodEnum.BiWeeklyMondaySunday2:
      return 'Bi-Weekly Monday-Sunday 2';
    case PayPeriodEnum.BiWeeklySundaySaturday1:
      return 'Bi-Weekly Sunday-Saturday 1';
    case PayPeriodEnum.BiWeeklySundaySaturday2:
      return 'Bi-Weekly Sunday-Saturday 2';
    case PayPeriodEnum.WeeklyMondaySunday:
      return 'Weekly Monday-Sunday';
    case PayPeriodEnum.WeeklyMondaySundayWedSubmit:
      return 'Weekly Monday-Sunday Wed Submit';
    case PayPeriodEnum.WeeklyThursdayWednesday:
      return 'Weekly Thursday-Wednesday';
    case PayPeriodEnum.WeeklySaturdayFriday:
      return 'Weekly Saturday-Friday';
    case PayPeriodEnum.WeeklySundaySaturday:
      return 'Weekly Sunday-Saturday';
    case PayPeriodEnum.Monthly:
      return 'Monthly';
    case PayPeriodEnum.SemiMonthly:
      return 'Semi-Monthly';

    default:
      return '';
  }
};

export const getCustomerInvoicingCycleOptions = () => [
  {
    value: InvoicingCycleEnum.Weekly,
    label: getCustomerInvoicingCycleLabel(InvoicingCycleEnum.Weekly),
  },
  {
    value: InvoicingCycleEnum.BiWeekly,
    label: getCustomerInvoicingCycleLabel(InvoicingCycleEnum.BiWeekly),
  },
  {
    value: InvoicingCycleEnum.Monthly,
    label: getCustomerInvoicingCycleLabel(InvoicingCycleEnum.Monthly),
  },
  {
    value: InvoicingCycleEnum.SemiMonthly,
    label: getCustomerInvoicingCycleLabel(InvoicingCycleEnum.SemiMonthly),
  },
  {
    value: InvoicingCycleEnum.SowWeekly,
    label: getCustomerInvoicingCycleLabel(InvoicingCycleEnum.SowWeekly),
  },
  {
    value: InvoicingCycleEnum.SowMonthly,
    label: getCustomerInvoicingCycleLabel(InvoicingCycleEnum.SowMonthly),
  },
];

export const getCustomerPayrollCycleOptions = () => [
  {
    value: PayPeriodEnum.BiWeeklyMondaySunday1,
    label: getCustomerPayrollCycleLabel(PayPeriodEnum.BiWeeklyMondaySunday1),
  },
  {
    value: PayPeriodEnum.BiWeeklyMondaySunday2,
    label: getCustomerPayrollCycleLabel(PayPeriodEnum.BiWeeklyMondaySunday2),
  },
  {
    value: PayPeriodEnum.BiWeeklySundaySaturday1,
    label: getCustomerPayrollCycleLabel(PayPeriodEnum.BiWeeklySundaySaturday1),
  },
  {
    value: PayPeriodEnum.BiWeeklySundaySaturday2,
    label: getCustomerPayrollCycleLabel(PayPeriodEnum.BiWeeklySundaySaturday2),
  },
  {
    value: PayPeriodEnum.WeeklyMondaySunday,
    label: getCustomerPayrollCycleLabel(PayPeriodEnum.WeeklyMondaySunday),
  },
  {
    value: PayPeriodEnum.WeeklyMondaySundayWedSubmit,
    label: getCustomerPayrollCycleLabel(
      PayPeriodEnum.WeeklyMondaySundayWedSubmit,
    ),
  },
  {
    value: PayPeriodEnum.WeeklyThursdayWednesday,
    label: getCustomerPayrollCycleLabel(PayPeriodEnum.WeeklyThursdayWednesday),
  },
  {
    value: PayPeriodEnum.WeeklySaturdayFriday,
    label: getCustomerPayrollCycleLabel(PayPeriodEnum.WeeklySaturdayFriday),
  },
  {
    value: PayPeriodEnum.WeeklySundaySaturday,
    label: getCustomerPayrollCycleLabel(PayPeriodEnum.WeeklySundaySaturday),
  },
  {
    value: PayPeriodEnum.Monthly,
    label: getCustomerPayrollCycleLabel(PayPeriodEnum.Monthly),
  },
  {
    value: PayPeriodEnum.SemiMonthly,
    label: getCustomerPayrollCycleLabel(PayPeriodEnum.SemiMonthly),
  },
];

export const getCommissionTypeApprovalLabel = (
  typeApproval?: CommissionTypeEnum,
) => {
  switch (typeApproval) {
    case CommissionTypeEnum.AdvancedCommission:
      return 'Funded - Advanced Commission';

    case CommissionTypeEnum.PaidWhenPaid:
      return 'Funded - Paid When Paid';

    case CommissionTypeEnum.SelfFunded:
      return 'Self-Funded';

    default:
      return '';
  }
};

export const getFundingTypeLabel = (type?: FundingTypeEnum) => {
  switch (type) {
    case FundingTypeEnum.Funded:
      return 'Funded';
    case FundingTypeEnum.SelfFunded:
      return 'Self-Funded';
    case FundingTypeEnum.Both:
      return 'Both';
    default:
      return '';
  }
};

type ClientDictionaryModel = {
  GeneralInfo: {
    CompanyInfo: {
      CompanyName: string;
      CompanyWebSite: string;
      CompanyEmail: string;
      CompanyPhoneNumber: string;
      AdditionalInvoiceName: string;
      Location: {
        Address: string;
        City: string;
        StateId: string;
        State: string;
        CountryId: string;
        Country: string;
        CountryCode: string;
        InternationalCityName: string;
        InternationalStateName: string;
        ZipCode: string;
      };
    };
    PrimaryContacts: {
      FirstName: string;
      LastName: string;
      Email: string;
      PhoneNumber: string;
      Title: string;
    };
  };
  AdditionalInfo: {
    PotentialHeadcount: string;
    AverageBillRate: string;
    EstimatedValue: string;
    EstimatedType: string;
    IndustryName: string;
    HaveBusinessBefore: string;
    YearsInBusiness: string;
    AbilityToPay: string;
  };
  AgreementDetailsInfo: {
    AgreementDetailsType: string;
    AgreementDetailsFileKey: string;
    AgreementDetailsFileName: string;
  };
  BillingInfo: {
    BillingContacts: {
      FirstName: string;
      LastName: string;
      Email: string;
      PhoneNumber: string;
    };
    Location: {
      Address: string;
      City: string;
      StateId: string;
      State: string;
      CountryId: string;
      Country: string;
      CountryCode: string;
      InternationalCityName: string;
      InternationalStateName: string;
      ZipCode: string;
    };
  };
};

export const getClientFieldName = (fieldName: string) => {
  try {
    const fieldNameWithoutIndex = fieldName.replace(/\[\d+\]/g, '');

    const keys = fieldNameWithoutIndex.split('.');

    const dictionary: ClientDictionaryModel = {
      GeneralInfo: {
        CompanyInfo: {
          CompanyName: 'Company name',
          CompanyWebSite: 'Company website',
          CompanyEmail: 'Company email',
          CompanyPhoneNumber: 'Phone number',
          AdditionalInvoiceName: 'Invoice Name (Legal/DBA)',
          Location: {
            Address: 'Main address',
            City: 'City',
            StateId: 'State',
            State: 'State',
            CountryId: 'Country',
            Country: 'Country',
            CountryCode: 'Country',
            InternationalCityName: 'City',
            InternationalStateName: 'State',
            ZipCode: 'ZIP code',
          },
        },
        PrimaryContacts: {
          FirstName: 'Primary contact first name',
          LastName: 'Primary contact last name',
          Email: ' Primary contact email',
          PhoneNumber: 'Primary contact phone number',
          Title: 'Primary contact title',
        },
      },
      AdditionalInfo: {
        PotentialHeadcount: 'Potential headcount',
        AverageBillRate: 'Average bill rate',
        EstimatedValue: 'Estimated length of the project',
        EstimatedType: 'Estimated type of the project',
        IndustryName: 'Industry',
        HaveBusinessBefore: 'Have you done business with them before',
        YearsInBusiness: 'Years in business',
        AbilityToPay: 'Ability to pay',
      },
      AgreementDetailsInfo: {
        AgreementDetailsType: 'Agreement details',
        AgreementDetailsFileKey: 'Agreement details',
        AgreementDetailsFileName: 'Agreement details',
      },
      BillingInfo: {
        BillingContacts: {
          FirstName: 'A/P first name',
          LastName: 'A/P last name',
          Email: 'A/P email',
          PhoneNumber: 'A/P phone number',
        },
        Location: {
          Address: 'Billing address',
          City: 'Billing city',
          StateId: 'Billing state',
          State: 'Billing state',
          CountryId: 'Billing country',
          Country: 'Billing country',
          CountryCode: 'Billing country',
          InternationalCityName: 'Billing city',
          InternationalStateName: 'Billing state',
          ZipCode: 'Billing ZIP code',
        },
      },
    };
    return keys?.reduce((acc: any, curr: string) => acc[curr], dictionary);
  } catch (e) {
    return false;
  }
};

export const getAgreementTypeLabel = (
  agreementType?: ServiceAgreementTypeEnum,
) => {
  switch (agreementType) {
    case ServiceAgreementTypeEnum.ClientAgreement:
      return 'Customer agreement';

    case ServiceAgreementTypeEnum.MemberAgreement:
      return 'Member agreement';

    case ServiceAgreementTypeEnum.MyBasePayAgreement:
      return 'myBasePay agreement';

    case ServiceAgreementTypeEnum.NotAvailable:
    default:
      return 'Not available';
  }
};

export const getAgreementFieldName = (agreement?: ServiceAgreementTypeEnum) => {
  switch (agreement) {
    case ServiceAgreementTypeEnum.ClientAgreement:
      return 'clientAgreement';
    case ServiceAgreementTypeEnum.MemberAgreement:
      return 'memberAgreement';
    case ServiceAgreementTypeEnum.MyBasePayAgreement:
      return 'myBasePayAgreement';
    case ServiceAgreementTypeEnum.NotAvailable:
      return 'isAgreementNotAvailableConfirmed';

    default:
      return '';
  }
};

export const getListOfCustomersWithoutAgreement = (
  allCustomers: CustomerBulkImportDetailsModel[],
) => {
  return allCustomers.filter((customer) =>
    isFileUploaderRequired(customer.agreementDetailsInfo.agreementDetailsType),
  );
};

export const getCustomerBreakInServiceLabel = (value?: BreakInServiceEnum) => {
  switch (value) {
    case BreakInServiceEnum.Days0:
      return '0 days';
    case BreakInServiceEnum.Days30:
      return '30 days';
    case BreakInServiceEnum.Days60:
      return '60 days';
    case BreakInServiceEnum.Days90:
      return '90 days';
    case BreakInServiceEnum.Days120:
      return '120 days';
    case BreakInServiceEnum.Days180:
      return '180 days';
    default:
      return '';
  }
};

export const getCustomerBreakInServiceValue = (value?: BreakInServiceEnum) => {
  switch (value) {
    case BreakInServiceEnum.Days0:
      return 0;
    case BreakInServiceEnum.Days30:
      return 30;
    case BreakInServiceEnum.Days60:
      return 60;
    case BreakInServiceEnum.Days90:
      return 90;
    case BreakInServiceEnum.Days120:
      return 120;
    case BreakInServiceEnum.Days180:
      return 180;
    default:
      return;
  }
};

export const getCustomerBreakInServiceEnumOptions = () => [
  {
    value: BreakInServiceEnum.Days0,
    label: getCustomerBreakInServiceLabel(BreakInServiceEnum.Days0),
  },
  {
    value: BreakInServiceEnum.Days30,
    label: getCustomerBreakInServiceLabel(BreakInServiceEnum.Days30),
  },
  {
    value: BreakInServiceEnum.Days60,
    label: getCustomerBreakInServiceLabel(BreakInServiceEnum.Days60),
  },
  {
    value: BreakInServiceEnum.Days90,
    label: getCustomerBreakInServiceLabel(BreakInServiceEnum.Days90),
  },
  {
    value: BreakInServiceEnum.Days120,
    label: getCustomerBreakInServiceLabel(BreakInServiceEnum.Days120),
  },
  {
    value: BreakInServiceEnum.Days180,
    label: getCustomerBreakInServiceLabel(BreakInServiceEnum.Days180),
  },
];

export const getCustomerAgreementTypeLabel = (
  value?: CustomerAgreementTypeEnum | null,
) => {
  switch (value) {
    case CustomerAgreementTypeEnum.NA:
      return 'N/A';
    case CustomerAgreementTypeEnum.MBPMSSA:
      return 'MBP MSSA';
    case CustomerAgreementTypeEnum.MBPAddendum:
      return 'MBP Addendum';
    case CustomerAgreementTypeEnum.MBPMMSAMemberModified:
      return 'MBP MMSA Member Modified';
    case CustomerAgreementTypeEnum.CustomerAgreement:
      return 'Customer agreement';
    case CustomerAgreementTypeEnum.MemberAgreement:
      return 'Member agreement';
    default:
      return '';
  }
};

export const getCustomerAgreementTypeOptions = () => [
  {
    value: CustomerAgreementTypeEnum.NA,
    label: getCustomerAgreementTypeLabel(CustomerAgreementTypeEnum.NA),
  },
  {
    value: CustomerAgreementTypeEnum.MBPMSSA,
    label: getCustomerAgreementTypeLabel(CustomerAgreementTypeEnum.MBPMSSA),
  },
  {
    value: CustomerAgreementTypeEnum.MBPAddendum,
    label: getCustomerAgreementTypeLabel(CustomerAgreementTypeEnum.MBPAddendum),
  },
  {
    value: CustomerAgreementTypeEnum.MBPMMSAMemberModified,
    label: getCustomerAgreementTypeLabel(
      CustomerAgreementTypeEnum.MBPMMSAMemberModified,
    ),
  },
  {
    value: CustomerAgreementTypeEnum.CustomerAgreement,
    label: getCustomerAgreementTypeLabel(
      CustomerAgreementTypeEnum.CustomerAgreement,
    ),
  },
  {
    value: CustomerAgreementTypeEnum.MemberAgreement,
    label: getCustomerAgreementTypeLabel(
      CustomerAgreementTypeEnum.MemberAgreement,
    ),
  },
];

export const getIndemnificationTypeLabel = (
  value?: IndemnificationTypeEnum | null,
) => {
  switch (value) {
    case IndemnificationTypeEnum.Mutual:
      return 'Mutual';
    case IndemnificationTypeEnum.MBPFavored:
      return 'MBP favored';
    case IndemnificationTypeEnum.CustomerFavored:
      return 'Customer favored';
    default:
      return '';
  }
};

export const getIndemnificationTypeOptions = () => [
  {
    value: IndemnificationTypeEnum.Mutual,
    label: getIndemnificationTypeLabel(IndemnificationTypeEnum.Mutual),
  },
  {
    value: IndemnificationTypeEnum.MBPFavored,
    label: getIndemnificationTypeLabel(IndemnificationTypeEnum.MBPFavored),
  },
  {
    value: IndemnificationTypeEnum.CustomerFavored,
    label: getIndemnificationTypeLabel(IndemnificationTypeEnum.CustomerFavored),
  },
];

export const getContractedMBPServiceLinesLabel = (
  value?: ContractedMBPServiceLinesEnum,
) => {
  switch (value) {
    case ContractedMBPServiceLinesEnum.EOR:
      return 'EOR';
    case ContractedMBPServiceLinesEnum.AOR:
      return 'AOR';
    case ContractedMBPServiceLinesEnum.VOR:
      return 'VOR';
    case ContractedMBPServiceLinesEnum.Other:
      return 'Other';
    default:
      return '';
  }
};

export const getContractedMBPServiceLinesOptions = () => [
  {
    value: ContractedMBPServiceLinesEnum.EOR,
    label: getContractedMBPServiceLinesLabel(ContractedMBPServiceLinesEnum.EOR),
  },
  {
    value: ContractedMBPServiceLinesEnum.AOR,
    label: getContractedMBPServiceLinesLabel(ContractedMBPServiceLinesEnum.AOR),
  },
  {
    value: ContractedMBPServiceLinesEnum.VOR,
    label: getContractedMBPServiceLinesLabel(ContractedMBPServiceLinesEnum.VOR),
  },
  {
    value: ContractedMBPServiceLinesEnum.Other,
    label: getContractedMBPServiceLinesLabel(
      ContractedMBPServiceLinesEnum.Other,
    ),
  },
];

export const getContractRenewalTermLabel = (
  value?: ContractRenewalTermEnum | null,
) => {
  switch (value) {
    case ContractRenewalTermEnum.Fixed:
      return 'Fixed';
    case ContractRenewalTermEnum.Evergreen:
      return 'Evergreen';
    case ContractRenewalTermEnum.AutoRenew:
      return 'Auto renew';
    default:
      return '';
  }
};

export const getContractRenewalTermOptions = () => [
  {
    value: ContractRenewalTermEnum.Fixed,
    label: getContractRenewalTermLabel(ContractRenewalTermEnum.Fixed),
  },
  {
    value: ContractRenewalTermEnum.Evergreen,
    label: getContractRenewalTermLabel(ContractRenewalTermEnum.Evergreen),
  },
  {
    value: ContractRenewalTermEnum.AutoRenew,
    label: getContractRenewalTermLabel(ContractRenewalTermEnum.AutoRenew),
  },
];

export const getPaymentMethodTypeLabel = (
  value?: PaymentMethodTypeEnum | null,
) => {
  switch (value) {
    case PaymentMethodTypeEnum.LiveCheck:
      return 'Live check';
    case PaymentMethodTypeEnum.ACH:
      return 'ACH';
    case PaymentMethodTypeEnum.AutoDebit:
      return 'Auto-debit';
    case PaymentMethodTypeEnum.CreditCard:
      return 'Credit card';
    default:
      return '';
  }
};

export const getPaymentMethodTypeOptions = () => [
  {
    value: PaymentMethodTypeEnum.LiveCheck,
    label: getPaymentMethodTypeLabel(PaymentMethodTypeEnum.LiveCheck),
  },
  {
    value: PaymentMethodTypeEnum.ACH,
    label: getPaymentMethodTypeLabel(PaymentMethodTypeEnum.ACH),
  },
  {
    value: PaymentMethodTypeEnum.AutoDebit,
    label: getPaymentMethodTypeLabel(PaymentMethodTypeEnum.AutoDebit),
  },
  {
    value: PaymentMethodTypeEnum.CreditCard,
    label: getPaymentMethodTypeLabel(PaymentMethodTypeEnum.CreditCard),
  },
];

export const isFileUploaderRequired = (agreement?: ServiceAgreementTypeEnum) =>
  agreement === ServiceAgreementTypeEnum.ClientAgreement ||
  agreement === ServiceAgreementTypeEnum.MemberAgreement ||
  agreement === ServiceAgreementTypeEnum.MyBasePayAgreement;

type AgreementDetailsWithSigner = {
  agreementDetailsType?: ServiceAgreementTypeEnum;
  agreementAttachments?: unknown[] | null;
};

export const isContractSignerSectionActiveForAgreementDetails = (
  agreementDetailsInfo?: AgreementDetailsWithSigner,
): boolean => {
  if (!agreementDetailsInfo?.agreementDetailsType) {
    return false;
  }

  if (!isFileUploaderRequired(agreementDetailsInfo.agreementDetailsType)) {
    return false;
  }

  return !!agreementDetailsInfo.agreementAttachments?.length;
};
